// Extract a user-friendly "reason" string from any thrown value (axios error,
// JS Error, plain string, …). Used by the TokenFailPage to explain why the
// app redirected the user to /tokenfail instead of just saying "not
// authorised". Never returns secrets — only HTTP status / message text we
// were going to log to the console anyway.

const FALLBACK = "We could not load this form. The link may be invalid or expired.";

export const describeRequestError = (error, fallback = FALLBACK) => {
  if (!error) return fallback;

  if (typeof error === "string") return error;

  // Axios-style error: `error.response` is the server's reply.
  const response = error.response;
  if (response) {
    const status = response.status;
    const data = response.data;

    // 1) Our own validator shape: { errors: [{ field, message }, ...] }
    if (Array.isArray(data?.errors) && data.errors.length > 0) {
      const messages = data.errors
        .map((e) => e?.message)
        .filter(Boolean);
      if (messages.length > 0) return messages.join(" ");
    }

    // 2) ASP.NET ValidationProblemDetails: { errors: { Field: [msg, ...] } }
    if (data?.errors && typeof data.errors === "object") {
      const messages = Object.values(data.errors)
        .flat()
        .filter((m) => typeof m === "string");
      if (messages.length > 0) return messages.join(" ");
    }

    // 3) Plain text / fallback message from the server.
    if (typeof data === "string" && data.trim()) return data.trim();
    if (typeof data?.message === "string" && data.message.trim()) return data.message.trim();
    if (typeof data?.title === "string" && data.title.trim()) return data.title.trim();

    // 4) Status-only fallback so support has something to go on.
    if (status === 401 || status === 403) {
      return "The link is no longer authorised (HTTP " + status + "). It may have expired or been revoked.";
    }
    if (status === 404) {
      return "The form could not be found (HTTP 404). The link may be invalid.";
    }
    if (status >= 500) {
      return "The Hafnia service is currently unavailable (HTTP " + status + "). Please try again shortly.";
    }
    if (status) {
      return "The Hafnia service returned an unexpected response (HTTP " + status + ").";
    }
  }

  // Network failure / no response received.
  if (error.request && !response) {
    return "We could not reach the Hafnia service. Please check your connection and try again.";
  }

  if (typeof error.message === "string" && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};
